import { useCallback, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { usePinTrigger } from "../hooks/usePinTrigger";
import OTPModal from "../modal/OTPModal";
import { closeApp } from "../utils/closeApp";
import { clearTicketDetailFromStorage } from "../utils/ticket";

const PIN_EXCLUDE_SELECTORS = [
  "button",
  "input",
  "select",
  "textarea",
  "a",
  ".modal",
];

/** Idle / kiosk home route */
const isHomePath = (pathname: string) =>
  (pathname || "/").replace(/\/+$/, "") === "" || pathname === "/";

const clearSessionStorage = () => {
  clearTicketDetailFromStorage();
  try {
    localStorage.removeItem("selectedCan");
  } catch {
    /* ignore */
  }
};

const GlobalPinHandler = () => {
  const [showPinModal, setShowPinModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  // Triple-click (desktop) or 3-finger hold for 5s (kiosk touch) → PIN modal
  usePinTrigger({
    onTrigger: () => setShowPinModal(true),
    excludeSelectors: PIN_EXCLUDE_SELECTORS,
    clickDelay: 500,
    holdDuration: 5000,
  });

  const handlePinSuccess = useCallback(() => {
    setShowPinModal(false);

    // On idle/home: close Android kiosk app (WebView finish / exit)
    if (isHomePath(pathRef.current)) {
      clearSessionStorage();
      toast.success("PIN verified. Closing the app.");
      // Defer so modal teardown does not block the Android bridge call
      window.setTimeout(() => closeApp(), 50);
      return;
    }

    // On any inner page: clear session and return to app home (idle)
    clearSessionStorage();
    toast.success("PIN verified. Returning to the home screen.");
    navigate("/", { replace: true });
  }, [navigate]);

  return (
    <OTPModal
      show={showPinModal}
      onHide={() => setShowPinModal(false)}
      onSuccess={handlePinSuccess}
    />
  );
};

export default GlobalPinHandler;
